import { isFulfilled, type Middleware } from '@reduxjs/toolkit';

import analytics from './analytics';
import { ANALYTICS_EVENTS } from './constants';

type Fields = Record<string, unknown>;

interface MutationEvent {
  event: string;
  properties: Fields;
}

type BuildMutationEvent = (args: Fields, response: Fields) => MutationEvent | null;

const readFields = (value: unknown): Fields => (typeof value === 'object' && value !== null ? (value as Fields) : {});

/** Every chat send is a FormData; only the ones carrying a file have an attachment. */
const hasAttachedFile = (body: unknown): boolean => body instanceof FormData && body.get('file') instanceof Blob;

const buildMessageSentEvent: BuildMutationEvent = (args) => ({
  event: ANALYTICS_EVENTS.MESSAGE_SENT,
  properties: { clientId: args.clientId, bulk: false, hasAttachment: hasAttachedFile(args.body) },
});

/** Discards send `disableClientAiResponses: undefined`, which JSON drops, so only fields with a value were updated. */
/**
 * `disableClientAiResponses` is the AI switch and is always sent alone: null turns the AI on, 0 turns it off for good
 * and a number turns it off for that many hours. Discards send it as undefined when they leave the AI as it was.
 */
const buildClientUpdatedEvent: BuildMutationEvent = (args) => {
  const body = readFields(args.body);
  const aiSwitch = body.disableClientAiResponses;
  if (aiSwitch === null) return { event: ANALYTICS_EVENTS.CLIENT_AI_ENABLED, properties: { clientId: args.id } };
  if (typeof aiSwitch === 'number') {
    const properties = { clientId: args.id, permanent: aiSwitch === 0, hours: aiSwitch || null };
    return { event: ANALYTICS_EVENTS.CLIENT_AI_DISABLED, properties };
  }
  const fields = Object.keys(body).filter((field) => body[field] !== undefined);
  if (fields.length === 0) return null;
  return { event: ANALYTICS_EVENTS.CLIENT_UPDATED, properties: { clientId: args.id, fields } };
};

/** Clock difference allowed between the browser and the backend when telling whether a campaign starts now. */
const CAMPAIGN_START_TOLERANCE_MS = 5 * 60 * 1000;

const readCount = (value: unknown): number => (typeof value === 'number' ? value : 0);

/**
 * The backend sets the first batch to now when the campaign starts today inside its hours, so only a later first
 * batch is scheduled. With no first batch nothing was accepted, and that still counts as sent.
 */
const isCampaignScheduled = (response: Fields): boolean => {
  const firstBatchAt = readFields(response.scheduleSummary).firstBatchAt;
  if (typeof firstBatchAt !== 'string') return false;
  return Date.parse(firstBatchAt) > Date.now() + CAMPAIGN_START_TOLERANCE_MS;
};

/** The file flow returns its recipients as `queued` and the list flow as `scheduled`; `accepted` adds both. */
const buildBulkTemplateEvent =
  (fromFile: boolean): BuildMutationEvent =>
  (_args, response) => ({
    event: isCampaignScheduled(response)
      ? ANALYTICS_EVENTS.BULK_TEMPLATE_SCHEDULED
      : ANALYTICS_EVENTS.BULK_TEMPLATE_SENT,
    properties: {
      fromFile,
      requested: response.totalRequested,
      accepted: readCount(response.scheduled) + readCount(response.queued),
      rejected: response.rejected,
    },
  });

/**
 * The event each tracked RTK Query mutation sends once it succeeds, by endpoint name. Hooking the mutations instead
 * of their buttons covers every screen that calls them, and a failed request sends nothing.
 * Only ids, counts and field names are sent: never message text, note content or client data.
 */
const MUTATION_EVENTS: Partial<Record<string, BuildMutationEvent>> = {
  postMessage: buildMessageSentEvent,
  postMessageV2: buildMessageSentEvent,
  bulkSendClientMessages: (_args, response) => ({
    event: ANALYTICS_EVENTS.MESSAGE_SENT,
    properties: { bulk: true, requested: response.requested, sent: response.sent },
  }),
  postTemplateMessage: (args) => ({
    event: ANALYTICS_EVENTS.TEMPLATE_SENT,
    properties: { clientId: args.clientId, bulk: false, templateType: args.templateType },
  }),
  sendTemplate: (args, response) => ({
    event: args.scheduleDate ? ANALYTICS_EVENTS.TEMPLATE_SCHEDULED : ANALYTICS_EVENTS.TEMPLATE_SENT,
    properties: { clientId: response.clientId, bulk: false },
  }),
  sendBulkTemplate: buildBulkTemplateEvent(false),
  sendBulkFileTemplate: buildBulkTemplateEvent(true),
  createProjectTemplate: () => ({ event: ANALYTICS_EVENTS.TEMPLATE_CREATED, properties: {} }),
  deleteProjectTemplate: () => ({ event: ANALYTICS_EVENTS.TEMPLATE_DELETED, properties: {} }),
  updateTemplateCatalog: () => ({ event: ANALYTICS_EVENTS.TEMPLATE_CATALOG_UPDATED, properties: {} }),
  cancelScheduledTemplate: () => ({
    event: ANALYTICS_EVENTS.SCHEDULED_TEMPLATE_CANCELLED,
    properties: { batch: false },
  }),
  cancelTemplateBatch: () => ({ event: ANALYTICS_EVENTS.BULK_TEMPLATE_CANCELLED, properties: {} }),
  createClientNote: (args) => ({
    event: ANALYTICS_EVENTS.CLIENT_NOTE_CREATED,
    properties: { clientId: args.clientId, bulk: false, count: 1 },
  }),
  bulkCreateClientNotes: (_args, response) => ({
    event: ANALYTICS_EVENTS.CLIENT_NOTE_CREATED,
    properties: { bulk: true, count: response.created },
  }),
  // /clients/new, opened from a CRM link: the backend creates the client or updates the existing one.
  putClient: () => ({ event: ANALYTICS_EVENTS.CLIENT_OPENED_FROM_CRM, properties: {} }),
  updateEditableAttributes: buildClientUpdatedEvent,
  updateAdminEditableAttributes: buildClientUpdatedEvent,
  updateClientAssignedUser: (args) => ({
    event: ANALYTICS_EVENTS.CLIENTS_ASSIGNED,
    properties: { clientId: args.clientId, bulk: false, count: 1 },
  }),
  bulkAssignClientsUser: (args, response) => ({
    event: ANALYTICS_EVENTS.CLIENTS_ASSIGNED,
    properties: { bulk: true, count: response.updated, unassigned: args.userId === null },
  }),
  bulkMoveClientsToStage: (args, response) => ({
    event: ANALYTICS_EVENTS.CLIENTS_BULK_MOVED,
    properties: { requested: response.requested, moved: response.updated, disableAi: Boolean(args.disableAi) },
  }),
  confirmClientBulkImport: (_args, response) => ({
    event: ANALYTICS_EVENTS.CLIENTS_BULK_IMPORTED,
    properties: {
      created: response.created,
      updated: response.updated,
      unchanged: response.unchanged,
      rejected: response.rejected,
      testClients: response.testClients,
    },
  }),
  bulkSendCallPermission: (_args, response) => ({
    event: ANALYTICS_EVENTS.CALL_PERMISSION_REQUESTED,
    properties: { bulk: true, requested: response.requested, sent: response.sent },
  }),
};

/**
 * Sends the analytics event of every successful mutation listed in MUTATION_EVENTS.
 *
 * @returns the Redux middleware
 */
const mutationEventsMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action);
  if (!isFulfilled(action)) return result;
  const meta = readFields(action.meta);
  const request = readFields(meta.arg);
  if (request.type !== 'mutation' || typeof request.endpointName !== 'string') return result;
  const buildEvent = MUTATION_EVENTS[request.endpointName];
  if (!buildEvent) return result;
  const args = readFields(request.originalArgs);
  const response = readFields(action.payload);
  const mutationEvent = buildEvent(args, response);
  if (mutationEvent) analytics.captureEvent(mutationEvent.event, mutationEvent.properties);
  return result;
};

export default mutationEventsMiddleware;
